import { withTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import SaveIcon from "@mui/icons-material/Save";
import ResetIcon from "@mui/icons-material/RotateLeft";
import { alpha } from "@mui/material/styles";
import { red } from "@mui/material/colors";
import { isEmpty } from "lodash";

import codeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import AppAutocomplete from "@plannel/components/ui/AppAutocomplete";
import SegmentedControl from "@plannel/components/ui/SegmentedControl";
import StatPill from "@plannel/components/ui/StatPill";
import { CARD_HEADER_SX } from "@plannel/components/ui/AppCard";
import { AddButton, RemoveButton } from "@plannel/components/ActionIconButton";
import { FS } from "@plannel/utils/font-util";

/*
  선택한 직책 하나의 접근 규칙 패널.

  기존에는 전 직책의 ACL 이 한 그리드에 평평하게 쌓여 있어서, "이 직책이 무엇을 볼 수 있나"를
  알려면 postnName 컬럼을 눈으로 훑어야 했다. 여기서는 계층 보드에서 고른 직책 하나만 다룬다.
    · 위: 고른 규칙의 편집기(대상 계층 + Owner/Sub/Others 권한)
    · 아래: 이 직책의 규칙 목록. 카드를 고르면 위 편집기가 그 규칙을 잡는다.
  권한 세 줄은 그리드 셀 드롭다운이 아니라 세그먼트라, 세 값의 대소관계(Owner ≥ Sub ≥ Others)가
  펼쳐진 채로 보인다 — 저장 눌러서 경고를 보고서야 아는 일이 줄어든다.
*/
const PERM_ROWS = [
  { field: "ownerPermission", labelKey: "ownerHrchyPermission" },
  { field: "groupPermission", labelKey: "subHrchyPermission" },
  { field: "othersPermission", labelKey: "othersHrchyPermission" },
];

const PERM_OPTIONS = codeUtil.dpPermission;

// 권한 배지 — 읽기/쓰기는 색을 갖고, none 은 점선 윤곽으로 "비어 있음"을 보여준다.
const PermBadge = ({ t, label, value }) => {
  const auth = PERM_OPTIONS.find(option => option.id === String(value))?.auth ?? "none";
  const isNone = auth === "none";
  const isFull = auth === "readWrite";
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
      <Box sx={{ fontSize: FS.CAPTION, color: "text.secondary", flexShrink: 0 }}>{label}</Box>
      <Box sx={{
        fontSize: FS.CAPTION, lineHeight: 1, px: 0.75, py: "3px", borderRadius: 0.75, whiteSpace: "nowrap",
        border: 1,
        borderStyle: isNone ? "dashed" : "solid",
        borderColor: isFull ? "primary.main" : "divider",
        bgcolor: isFull ? theme => alpha(theme.palette.primary.main, 0.1) : (isNone ? "transparent" : "grey.100"),
        color: isFull ? "primary.dark" : "text.secondary",
        fontWeight: isNone ? 400 : 600,
      }}>
        {t(auth)}
      </Box>
    </Box>
  );
};

const PositionRulePanel = ({
  t, postn, postnPath, ruleCount, tab, onTabChange,
  rules, selectedRuleKey, onSelectRule, onPatchRule,
  customerHrchyOptions, itemHrchyOptions, measureOptions,
  onAdd, onRemove, onSave, onReset, dirtyCount,
}) => {
  const isMeasureTab = tab === codeUtil.dpAccessControlTab.MEASURE;
  const selectedRule = rules.find(rule => rule._key === selectedRuleKey) ?? null;

  const findOption = (options, key) => (options ?? []).find(option => option.key === key) ?? null;

  const sectionTitleSx = { ...CARD_HEADER_SX, fontSize: FS.TITLE };

  const renderSubject = () => {
    if (isMeasureTab) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Box sx={{ fontSize: FS.BODY, color: "text.secondary" }}>{t("measureName")}</Box>
          <AppAutocomplete
            options={measureOptions}
            value={findOption(measureOptions, selectedRule?.measureCommonCd)}
            onChange={(_e, option) => onPatchRule(selectedRuleKey, { measureOption: option })}
            disabled={!selectedRule}
            placeholder={t("measureName")}
          />
        </Box>
      );
    }
    return (
      <Box sx={{ display: "flex", gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Box sx={{ fontSize: FS.BODY, color: "text.secondary" }}>{t("customerHrchyName")}</Box>
          <AppAutocomplete
            options={customerHrchyOptions}
            value={findOption(customerHrchyOptions, selectedRule?.customerHrchyCd)}
            onChange={(_e, option) => onPatchRule(selectedRuleKey, { customerHrchyOption: option })}
            disabled={!selectedRule}
            placeholder={t("customerHrchyName")}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Box sx={{ fontSize: FS.BODY, color: "text.secondary" }}>{t("itemHrchyNm")}</Box>
          <AppAutocomplete
            options={itemHrchyOptions}
            value={findOption(itemHrchyOptions, selectedRule?.itemHrchyCd)}
            onChange={(_e, option) => onPatchRule(selectedRuleKey, { itemHrchyOption: option })}
            disabled={!selectedRule}
            placeholder={t("itemHrchyNm")}
          />
        </Box>
      </Box>
    );
  };

  if (!postn) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", p: 3 }}>
        <Box sx={{ fontSize: FS.TITLE, color: "text.secondary", textAlign: "center" }}>{t("selectPostn")}</Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      {/* 선택한 직책 — 이름·최종승인 여부·루트부터의 경로. 보드에서 눈을 떼도 무엇을 편집 중인지 남는다. */}
      <Box sx={{ p: 1.5, pb: 1.25, flexShrink: 0 }}>
        <Box sx={{ fontSize: FS.BODY, color: "text.secondary" }}>{t("selectedPostn")}</Box>
        <Box sx={{ fontSize: FS.H1, fontWeight: 600, mt: 0.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {postn.name}
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
          {postn.finalApvlFlg && (
            <Box sx={{
              ...ColorUtil.tintChipSx(red[700]),
              fontSize: FS.CAPTION, lineHeight: 1, px: 0.75, py: "4px", borderRadius: 0.75,
            }}>
              {t("finalApvl")}
            </Box>
          )}
          <StatPill label={t("rule")} value={ruleCount} accent={ruleCount > 0} />
        </Box>
        {!isEmpty(postnPath) && (
          <Box sx={{ mt: 1, fontSize: FS.BODY, color: "text.secondary", wordBreak: "break-word" }}>
            {postnPath.join("  ›  ")}
          </Box>
        )}
      </Box>

      <Divider />

      <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1.25, flexShrink: 0 }}>
        <Box sx={sectionTitleSx}>{t("hrchyAccess")}</Box>
        {renderSubject()}
        {PERM_ROWS.map(row => (
          <Box key={row.field} sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Box sx={{ fontSize: FS.BODY, color: "text.secondary" }}>{t(row.labelKey)}</Box>
            {/*
              SegmentedControl 은 inline-flex 라 자기 폭만 쓰는 게 정상인데, 부모가 세로 flex 라
              stretch 되어 트랙(연회색 알약 배경)만 패널 끝까지 늘어난다. alignSelf 로 다시 오므린다.
            */}
            <SegmentedControl
              value={String(selectedRule?.[row.field] ?? "")}
              onChange={value => onPatchRule(selectedRuleKey, { [row.field]: value })}
              options={PERM_OPTIONS.map(option => ({ value: option.id, label: t(option.auth), disabled: !selectedRule }))}
              sx={{ alignSelf: "flex-start" }}
            />
          </Box>
        ))}
      </Box>

      <Divider />

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1, flexShrink: 0 }}>
        <Box sx={sectionTitleSx}>{t("accessRules")}</Box>
        {dirtyCount > 0 && <StatPill label={t("modified")} value={dirtyCount} accent />}
        <Box sx={{ flex: 1 }} />
        <AddButton onClick={onAdd} />
        <RemoveButton onClick={onRemove} disabled={!selectedRule} />
      </Box>

      <Box sx={{ px: 1.5, pb: 1, flexShrink: 0 }}>
        <SegmentedControl
          value={tab}
          onChange={onTabChange}
          options={[
            { value: codeUtil.dpAccessControlTab.CUSTHRCHY, label: t("hrchyAccessControl") },
            { value: codeUtil.dpAccessControlTab.MEASURE, label: t("measureAccessControl") },
          ]}
        />
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", px: 1.5, pb: 1.5, display: "flex", flexDirection: "column", gap: 0.75 }}>
        {isEmpty(rules) && (
          <Box sx={{ fontSize: FS.SUB, color: "text.secondary", textAlign: "center", py: 3 }}>{t("noAccessRule")}</Box>
        )}
        {rules.map(rule => {
          const selected = rule._key === selectedRuleKey;
          return (
            <Box
              key={rule._key}
              onClick={() => onSelectRule(rule._key)}
              sx={{
                boxSizing: "border-box",
                border: 1,
                borderColor: selected ? "primary.main" : "divider",
                borderRadius: 1.5,
                bgcolor: selected ? theme => alpha(theme.palette.primary.main, 0.06) : "background.paper",
                p: 1.25,
                cursor: "pointer",
                "&:hover": { borderColor: selected ? "primary.main" : "grey.400" },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, minWidth: 0 }}>
                <Box sx={{ flex: 1, minWidth: 0, fontSize: FS.SUB, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {/* 지표 라벨은 measureOptions 에서 가져온다 — t() 로 직접 풀면 grid 네임스페이스에만 있는 이름(marketingForecast)이 키 그대로 나온다. */}
                  {isMeasureTab ? (findOption(measureOptions, rule.measureCommonCd)?.label ?? "—") : (rule.customerHrchyName || "—")}
                </Box>
                {!isMeasureTab && (
                  <Box sx={{ flexShrink: 0, maxWidth: "45%", fontSize: FS.BODY, color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {rule.itemHrchyNm || "—"}
                  </Box>
                )}
                {rule._dirty && <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "primary.main", flexShrink: 0 }} />}
              </Box>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                <PermBadge t={t} label={t("owner")} value={rule.ownerPermission} />
                <PermBadge t={t} label={t("sub")} value={rule.groupPermission} />
                <PermBadge t={t} label={t("others")} value={rule.othersPermission} />
              </Box>
              {!isEmpty(rule.users) && (
                <Box sx={{ mt: 0.75, fontSize: FS.CAPTION, color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {`${t("user")} · ${rule.users.map(user => user.username).join(", ")}`}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {/*
        커밋 버튼은 패널 오른쪽 아래 고정이다. 규칙 목록 위 아이콘 행에 두면 추가/삭제 아이콘에 묻혀
        "저장했는지"가 안 보인다 — 스크롤되는 목록 밖, 시선이 마지막에 닿는 자리에 라벨과 함께 둔다.
      */}
      <Divider />
      {/* 버튼 규격은 BaseConfigModal 의 DialogActions 와 같다 — Reset 은 왼쪽 텍스트 버튼, Save 는 오른쪽 contained. */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 1.5, flexShrink: 0 }}>
        <Button size="small" startIcon={<ResetIcon />} onClick={onReset} sx={{ mr: "auto" }}>
          {t("reset")}
        </Button>
        <Button size="small" variant="contained" disableElevation startIcon={<SaveIcon />} onClick={onSave}>
          {t("save")}
        </Button>
      </Box>
    </Box>
  );
};

export default withTranslation()(PositionRulePanel);
